/**
 * Chat Screen Page
 * Main chat interface page
 */

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeProvider } from '@/design-system/themes';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Sidebar } from '@/components/navbar/web/Sidebar';
import { MobileNavbar } from '@/components/navbar/mobile/MobileNavbar';
import { ChatScreenContainer } from '@/components/chat';
import { HeroSection } from '@/components/landing-page-v2/HeroSection';
import { ExperienceListSection } from '@/components/landing-page-v2/ExperienceListSection';
import { NexgoJourneysSection } from '@/components/landing-page-v2/NexgoJourneysSection';
import { BoutiqueStaysSection } from '@/components/landing-page-v2/BoutiqueStaysSection';
import { GuidedActivitiesSection } from '@/components/landing-page-v2/GuidedActivitiesSection';
import { RidersCornerSection } from '@/components/landing-page-v2/RidersCornerSection';
import { MarketplaceBannerSection } from '@/components/landing-page-v2/MarketplaceBannerSection';
import { AboutUsSection } from '@/components/landing-page-v2/AboutUsSection';
import { FooterSection } from '@/components/landing-page-v2/FooterSection';
import {
  backgroundColors,
  spacing,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ChatLocation, ChatMessage } from '@/types/chatTypes';
import { useChatScreenStore } from '@/store/chatScreenStore';
import { useSearchStore } from '@/store/searchStore';
import { useRedirectStore } from '@/store/redirectStore';
import { useConversationMessages } from '@/hooks/useConversationMessages';
import { generateUserId } from '@/services/chatService';
import LoginModal from '@/components/common/LoginModal';
import SeoHead, {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_META_TITLE,
} from '../../components/common/SeoHead';

type ViewMode = 'chat' | 'landing-preview' | 'full-landing';

export default function ChatPage() {
  const [selectedLocation, setSelectedLocation] = useState<ChatLocation | null>(
    null
  );
  const [viewMode, setViewMode] = useState<ViewMode>('full-landing');
  const router = useRouter();
  const [initialQuery, setInitialQuery] = useState<string | null>(null);
  const [initialAudio, setInitialAudio] = useState<string | null>(null);
  const [initialResponse, setInitialResponse] = useState<string | null>(null);
  const [initialMessages, setInitialMessages] = useState<ChatMessage[] | null>(
    null
  );
  const [initialSessionId, setInitialSessionId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    getConversation,
    setActiveConversation,
    getActiveConversation,
    clearAllConversations,
    updateConversation,
  } = useChatScreenStore();
  const { isChatLoginRequired } = useSearchStore();
  const { setRedirectUrl, setLoginContext } = useRedirectStore();

  // State for loading conversation from API
  const [userId, setUserId] = useState<string | null>(null);
  const [conversationIdFromQuery, setConversationIdFromQuery] = useState<
    string | null
  >(null);

  // Ref to prevent infinite loops from store updates during restoration
  const isRestoringRef = useRef<string | null>(null);
  // Ref to track processed conversationIds to prevent re-processing
  const processedConversationIdRef = useRef<string | null>(null);
  // When true, user clicked "New Chat" - show new chat page and do not restore/fetch previous conversation
  const newChatRequestedRef = useRef(false);
  // Prevent fallback effect from applying landing view more than once (avoids update loops)
  const hasAppliedFallbackRef = useRef(false);

  // Get user ID on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const generatedUserId = generateUserId();
        setUserId(generatedUserId);
        console.log('🚀 [ChatPage] Component mounted:', {
          currentUrl: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          queryParams: router.query,
          userId: generatedUserId,
        });
      } catch (err) {
        console.error('❌ [ChatPage] Failed to generate user ID:', err);
      }
    }
  }, [router.query]);

  // ====================================================================
  // URL PROCESSING EFFECT - Handles all URL-based initialization
  // ====================================================================
  // This effect is responsible for parsing router.query and handling:
  // - conversationId (shared links/history) → API load
  // - responseKey/q/audioKey (sessionStorage) → direct chat
  // - view=full-landing → landing page
  // ====================================================================
  useEffect(() => {
    const {
      q,
      audioKey,
      responseKey,
      conversationId,
      view,
      new: newChatParam,
    } = router.query;

    // User clicked "New Chat" (from this page or from another page via ?new=1) - show new chat page
    if (
      newChatRequestedRef.current ||
      newChatParam === '1' ||
      newChatParam === 'true'
    ) {
      newChatRequestedRef.current = false;
      clearAllConversations();
      setConversationIdFromQuery(null);
      processedConversationIdRef.current = null;
      setInitialMessages(null);
      setInitialSessionId(null);
      setViewMode('landing-preview');
      setIsTransitioning(false);
      router.replace('/chat', undefined, { shallow: true });
      return;
    }

    // conversationId in URL: show existing chat (e.g. Discover from another page)
    if (conversationId && typeof conversationId === 'string') {
      if (processedConversationIdRef.current === conversationId) {
        const activeConversation = getActiveConversation();
        if (
          activeConversation?.id === conversationId &&
          activeConversation.messages.length > 0 &&
          initialMessages?.length === activeConversation.messages.length
        ) {
          return; // Already processed and loaded
        }
      }

      // Resolve conversation from store by id or by active conversation's sessionId
      const convById = getConversation(conversationId);
      const activeConversation = getActiveConversation();
      const convBySessionId =
        activeConversation &&
        (activeConversation.sessionId === conversationId ||
          activeConversation.id === conversationId)
          ? activeConversation
          : null;
      const localConversation = convById || convBySessionId;

      // Skip API fetch when we already have this conversation locally; set initialMessages so Discover and back-nav show content
      if (localConversation && localConversation.messages.length > 0) {
        const sessionIdToUse = localConversation.sessionId || conversationId;
        const normalizedMessages = localConversation.messages.map(msg => ({
          ...msg,
          session_id: sessionIdToUse,
          timestamp:
            msg.timestamp instanceof Date
              ? msg.timestamp
              : new Date(
                  typeof msg.timestamp === 'number'
                    ? msg.timestamp * 1000
                    : (msg.timestamp ?? Date.now())
                ),
        }));
        setViewMode('chat');
        setIsTransitioning(false);
        processedConversationIdRef.current = conversationId;
        setConversationIdFromQuery(null);
        setInitialMessages(normalizedMessages);
        setInitialSessionId(sessionIdToUse);
        return;
      }

      // Skip fetch when URL was just updated by us (ConversationsContainer sets this before router.replace)
      const internalUpdate =
        typeof window !== 'undefined'
          ? sessionStorage.getItem('_internal_conversation_update')
          : null;
      if (internalUpdate === 'true' || internalUpdate === conversationId) {
        const conv =
          getConversation(conversationId) ||
          (getActiveConversation()?.sessionId === conversationId
            ? getActiveConversation()
            : null);
        if (conv?.messages?.length) {
          const sessionIdToUse = conv.sessionId || conversationId;
          const normalizedMessages = conv.messages.map(msg => ({
            ...msg,
            session_id: sessionIdToUse,
            timestamp:
              msg.timestamp instanceof Date
                ? msg.timestamp
                : new Date(
                    typeof msg.timestamp === 'number'
                      ? msg.timestamp * 1000
                      : (msg.timestamp ?? Date.now())
                  ),
          }));
          setViewMode('chat');
          setIsTransitioning(false);
          processedConversationIdRef.current = conversationId;
          setConversationIdFromQuery(null);
          setInitialMessages(normalizedMessages);
          setInitialSessionId(sessionIdToUse);
          return;
        }
      }

      // Load from API (shared link or history)
      setViewMode('chat');
      setIsTransitioning(true);
      setConversationIdFromQuery(conversationId);
      processedConversationIdRef.current = conversationId;
      return;
    } else {
      setConversationIdFromQuery(null);
      processedConversationIdRef.current = null;
    }

    // Handle responseKey (highest priority to prevent duplicate API calls)
    if (responseKey && typeof responseKey === 'string') {
      const responseData = sessionStorage.getItem(responseKey);
      if (responseData) {
        if (q) setInitialQuery(decodeURIComponent(q as string));
        setInitialResponse(responseData);
        setIsTransitioning(true);
        setTimeout(() => {
          setViewMode('chat');
          sessionStorage.removeItem(responseKey);
          router.replace('/chat', undefined, { shallow: true });
          setIsTransitioning(false);
        }, 200);
        return;
      }
    }

    // Handle query parameter
    if (q && typeof q === 'string') {
      setInitialQuery(decodeURIComponent(q));
      setViewMode('chat');
      setIsTransitioning(true);
      setTimeout(() => {
        setViewMode('chat');
        router.replace('/chat', undefined, { shallow: true });
        setIsTransitioning(false);
      }, 200);
      return;
    }

    // Handle audioKey
    if (audioKey && typeof audioKey === 'string') {
      const audioData = sessionStorage.getItem(audioKey);
      if (audioData) {
        setInitialAudio(audioData);
        setIsTransitioning(true);
        setTimeout(() => {
          setViewMode('chat');
          sessionStorage.removeItem(audioKey);
          router.replace('/chat', undefined, { shallow: true });
          setIsTransitioning(false);
        }, 200);
        return;
      }
    }

    // Handle view=full-landing
    if (view === 'full-landing') {
      if (viewMode !== 'full-landing') {
        setInitialQuery(null);
        setInitialAudio(null);
        setInitialResponse(null);
        setInitialMessages(null);
        setInitialSessionId(null);
        setSelectedLocation(null);
        setViewMode('full-landing');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setTimeout(() => {
        router.replace('/chat', undefined, { shallow: true });
      }, 1000);
      return;
    }
  }, [
    router,
    router.query,
    getConversation,
    getActiveConversation,
    clearAllConversations,
    initialMessages,
    viewMode,
  ]);

  // ====================================================================
  // STORE RESTORATION EFFECT - Only restores from local store
  // ====================================================================
  useEffect(() => {
    if (newChatRequestedRef.current) return;

    const { q, audioKey, responseKey, conversationId, view } = router.query;
    const hasUrlParams = !!(
      q ||
      audioKey ||
      responseKey ||
      conversationId ||
      view
    );

    if (hasUrlParams || view === 'full-landing') {
      return;
    }

    if (initialMessages && initialMessages.length > 0) {
      if (viewMode !== 'chat') setViewMode('chat');
      return;
    }

    const activeConversation = getActiveConversation();

    if (activeConversation?.messages.length > 1) {
      const targetSessionId =
        activeConversation.sessionId || activeConversation.id;
      const needsRestore =
        !initialSessionId || initialSessionId !== targetSessionId;

      if (needsRestore && isRestoringRef.current !== activeConversation.id) {
        isRestoringRef.current = activeConversation.id;

        setActiveConversation(activeConversation.id);

        // Ensure session_id and valid Date timestamps (store may have serialized values)
        const messagesWithSessionId = activeConversation.messages.map(msg => ({
          ...msg,
          session_id: msg.session_id || targetSessionId,
          timestamp:
            msg.timestamp instanceof Date
              ? msg.timestamp
              : new Date(
                  typeof msg.timestamp === 'number'
                    ? msg.timestamp * 1000
                    : (msg.timestamp ?? Date.now())
                ),
        }));

        // Update store if session_id values changed
        const needsUpdate = activeConversation.messages.some(
          msg => msg.session_id !== targetSessionId
        );

        if (needsUpdate) {
          setTimeout(() => {
            updateConversation(activeConversation.id, messagesWithSessionId);
          }, 0);
        }

        setInitialMessages(messagesWithSessionId);
        setInitialSessionId(targetSessionId);
        setViewMode('chat');
        setIsTransitioning(false);

        setTimeout(() => {
          isRestoringRef.current = null;
        }, 100);
      }
    }
  }, [
    router.query,
    viewMode,
    initialMessages,
    initialSessionId,
    getActiveConversation,
    setActiveConversation,
    updateConversation,
  ]);

  // Fallback: if chat view has no messages and we're not loading, show landing to avoid blank page
  useEffect(() => {
    if (viewMode !== 'chat' || conversationIdFromQuery || isTransitioning) {
      if (viewMode !== 'chat') hasAppliedFallbackRef.current = false;
      return;
    }
    if (hasAppliedFallbackRef.current) return;
    const hasMessages =
      (initialMessages && initialMessages.length > 0) ||
      (getActiveConversation()?.messages?.length ?? 0) > 0;
    if (hasMessages) return;

    const t = setTimeout(() => {
      if (hasAppliedFallbackRef.current) return;
      const stillNoMessages =
        !(initialMessages && initialMessages.length > 0) &&
        !(getActiveConversation()?.messages?.length ?? 0);
      if (stillNoMessages) {
        hasAppliedFallbackRef.current = true;
        setViewMode('landing-preview');
        setInitialMessages(null);
        setInitialSessionId(null);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [
    viewMode,
    conversationIdFromQuery,
    isTransitioning,
    initialMessages,
    getActiveConversation,
  ]);

  // Fetch conversation messages from API when conversationId is present
  // When conversationId is in URL (shared link), use the conversationId endpoint
  const isSharedConversation = !!conversationIdFromQuery;
  const {
    messages: conversationMessages,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useConversationMessages(
    userId,
    conversationIdFromQuery,
    isSharedConversation // Use conversationId endpoint for shared URLs
  );

  // Convert API messages to ChatMessage format and load them
  useEffect(() => {
    if (
      conversationMessages &&
      conversationIdFromQuery &&
      !isLoadingMessages &&
      !messagesError
    ) {
      console.log('📥 [ChatPage] Loading conversation messages from API:', {
        conversationId: conversationIdFromQuery,
        messageCount: conversationMessages.messages.length,
      });

      // Convert API messages to ChatMessage format
      const chatMessages: ChatMessage[] = conversationMessages.messages.map(
        (msg, index) => {
          const timestamp = msg.timestamp
            ? new Date(msg.timestamp * 1000)
            : new Date();

          // Extract content from parts array (usually first text part)
          const textPart = msg.parts?.find(part => part.type === 'text');
          const content = textPart?.content || '';

          // Map author to role: 'user' -> 'user', 'root_agent' -> 'assistant'
          const role = msg.author === 'user' ? 'user' : 'assistant';

          // Use the conversationId (conversation id) as the session_id
          // This ensures all messages in the loaded conversation use the conversation id as session_id
          const messageSessionId =
            conversationIdFromQuery || conversationMessages.session_id;

          return {
            id: msg.id || `msg_${index}_${Date.now()}`,
            role,
            content,
            timestamp,
            session_id: messageSessionId, // All messages use the conversation id as session_id
            locations: msg.locations || undefined,
            followUpQuestions: msg.followUpQuestions || undefined,
            mood: msg.mood || undefined,
            intent: msg.intent || undefined,
            overview: msg.overview || undefined,
            experiences: msg.experiences || undefined,
            transcription: msg.transcription || undefined,
          };
        }
      );

      // Set initial messages and sessionId
      // Use the conversationId (conversation id) as the session_id for this conversation
      // This ensures all subsequent messages in this conversation use the same session_id
      const sessionIdToUse =
        conversationIdFromQuery || conversationMessages.session_id;
      setInitialMessages(chatMessages);
      setInitialSessionId(sessionIdToUse);

      // Update local store with API data to ensure consistency
      // This prevents conflicts between local and API data
      setActiveConversation(conversationIdFromQuery);
      updateConversation(conversationIdFromQuery, chatMessages);

      console.log(
        '✅ [ChatPage] Loaded conversation with session_id:',
        sessionIdToUse,
        '(using conversationId as session_id)'
      );

      // ViewMode is already set to 'chat' when conversationId is detected
      // Just clean up URL and transition
      setTimeout(() => {
        router.replace('/chat', undefined, { shallow: true });
        setIsTransitioning(false);
      }, 200);

      // Clear conversationIdFromQuery to prevent reloading
      setConversationIdFromQuery(null);
    } else if (messagesError) {
      console.error(
        '❌ [ChatPage] Error loading conversation messages:',
        messagesError
      );
      // Stop loading state on error so user isn't stuck
      setIsTransitioning(false);
      setConversationIdFromQuery(null);
      // Optional: You might want to show a toast here
      // toast.error("Failed to load conversation");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversationMessages,
    conversationIdFromQuery,
    isLoadingMessages,
    messagesError,
  ]);

  // Login requirement check removed - no longer forcing login after 2 chats

  const handleGoToLogin = () => {
    const redirectPath = router.asPath;
    const activeConversation = getActiveConversation();
    console.log('🔐 [ChatPage] Login required - Setting redirect:', {
      redirectPath,
      fullUrl: typeof window !== 'undefined' ? window.location.href : 'N/A',
      activeConversationId: activeConversation?.id || 'none',
      activeConversationMessagesCount: activeConversation?.messages.length || 0,
      queryParams: router.query,
    });
    setRedirectUrl(redirectPath);
    setLoginContext('chat');
    window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/v1/login`;
  };

  const handleNewChat = () => {
    newChatRequestedRef.current = true;
    clearAllConversations();
    router.replace('/chat', undefined, { shallow: true });

    setInitialQuery(null);
    setInitialAudio(null);
    setInitialResponse(null);
    setInitialMessages(null);
    setInitialSessionId(null);
    setSelectedLocation(null);

    setIsTransitioning(true);
    setViewMode('landing-preview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handleLogoClick = () => {
    // Clear all conversations from store - empty store means nothing to restore
    clearAllConversations();

    // Clear state immediately
    setInitialQuery(null);
    setInitialAudio(null);
    setInitialResponse(null);
    setInitialMessages(null);
    setInitialSessionId(null);
    setSelectedLocation(null);

    // Set view mode to full landing IMMEDIATELY (before navigation)
    setViewMode('full-landing');

    // Navigate to landing page with view parameter
    // Use push instead of replace to ensure the URL change is processed
    router.push('/chat?view=full-landing', undefined, { shallow: true });

    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Flag will be reset when user starts a new chat or navigates away
  };

  // Track viewMode changes
  const prevViewModeRef = useRef<ViewMode>(viewMode);
  if (prevViewModeRef.current !== viewMode) {
    prevViewModeRef.current = viewMode;
  }

  return (
    <ThemeProvider defaultMode='light'>
      <SeoHead
        title={DEFAULT_META_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        path='/chat'
        disableTitleSuffix
      />
      <div
        style={{
          height: viewMode === 'chat' ? 'calc(var(--vh, 1vh) * 100)' : 'auto',
          minHeight:
            viewMode === 'chat'
              ? 'calc(var(--vh, 1vh) * 100)'
              : 'calc(var(--vh, 1vh) * 100)',
          backgroundColor: backgroundColors.primary,
          display: 'flex',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          overflowY: viewMode === 'chat' ? 'hidden' : 'auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Sidebar */}
        <Sidebar onNewChat={handleNewChat} onLogoClick={handleLogoClick} />

        {/* Mobile Navbar */}
        <div className='md:hidden'>
          <MobileNavbar
            onNewChat={handleNewChat}
            onLogoClick={handleLogoClick}
          />
        </div>

        {/* Main Content */}
        <main
          style={{
            padding:
              viewMode === 'full-landing' || viewMode === 'landing-preview'
                ? 0
                : spacing[4],
            flex: 1,
            minWidth: 0,
            display: 'flex',
            gap: spacing[4],
            justifyContent: 'center', // Always center the content
            alignItems: 'stretch',
            overflowX: 'hidden',
            overflowY: viewMode === 'chat' ? 'hidden' : 'auto',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            height: viewMode === 'chat' ? 'calc(var(--vh, 1vh) * 100)' : 'auto',
          }}
          className={`md:ml-[106px] ${viewMode === 'full-landing' || viewMode === 'landing-preview' ? 'p-0' : 'p-2 md:p-4'} flex-col md:flex-row gap-2 md:gap-4 w-full max-w-full overflow-x-hidden ${viewMode === 'chat' ? 'overflow-y-hidden' : 'overflow-y-auto'} box-border pt-[80px] md:pt-0`}
        >
          {/* Loading Overlay during transition (only show when going TO chat, not FROM chat) */}
          <AnimatePresence>
            {isTransitioning && viewMode === 'chat' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: backgroundColors.primary,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing[4],
                  zIndex: 1000,
                  backdropFilter: 'blur(4px)',
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    border: `3px solid ${typographyColors.secondary}`,
                    borderTopColor: typographyColors.primary,
                    borderRightColor: typographyColors.primary,
                  }}
                />
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.base,
                    fontWeight: typography.weight.medium,
                    color: typographyColors.primary,
                    margin: 0,
                  }}
                >
                  Loading your results...
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode='wait' initial={false}>
            {/* Landing Preview - Hero + Experience List */}
            {(viewMode === 'landing-preview' ||
              viewMode === 'full-landing') && (
              <motion.div
                key='landing'
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -30, scale: 0.96 }}
                transition={{
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className='w-full overflow-x-hidden'
                style={{
                  width: '100%',
                  height:
                    viewMode === 'landing-preview'
                      ? 'calc(var(--vh, 1vh) * 100)'
                      : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    flex: viewMode === 'landing-preview' ? 1 : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    marginTop: isMobile ? 60 : 0,
                  }}
                >
                  <HeroSection />
                  <ExperienceListSection />
                </div>

                {/* Full Landing - Show all sections */}
                {viewMode === 'full-landing' && (
                  <>
                    <NexgoJourneysSection />
                    <BoutiqueStaysSection />
                    <GuidedActivitiesSection />
                    <RidersCornerSection />
                    <MarketplaceBannerSection />
                    <AboutUsSection />
                    <FooterSection />
                  </>
                )}
              </motion.div>
            )}

            {/* Chat Interface */}
            {viewMode === 'chat' && (
              <motion.div
                key='chat'
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -30, scale: 0.96 }}
                transition={{
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  maxWidth: '100%',
                  width: '100%',
                  position: 'relative',
                  alignItems: 'center', // Center the child horizontally (cross-axis for column)
                  // justifyContent not needed - we only need horizontal centering
                }}
                className='gap-2 md:gap-4 pt-4 md:pt-0'
              >
                <ChatScreenContainer
                  initialQuery={initialQuery}
                  initialAudio={initialAudio}
                  initialResponse={initialResponse}
                  initialMessages={initialMessages}
                  initialSessionId={initialSessionId}
                  initialSelectedLocation={selectedLocation}
                  onPlaceSelect={location => setSelectedLocation(location)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {isChatLoginRequired && (
        <LoginModal
          onLogin={() => {
            handleGoToLogin();
          }}
        />
      )}
    </ThemeProvider>
  );
}
